import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../base/table/table";
import { cn } from "../../../utils";
import { DataTablePagination } from "./DataTablePagination";
import { DataTableSkeleton } from "./DataTableSkeleton";
import { DataTableEmpty } from "./DataTableEmpty";
import { DataTableToolbar } from "./DataTableToolbar";
import { DataTableFilterChip } from "./DataTableFilterChip";
import { useParseUrlState, useSyncTableStateToUrl } from "./useDataTableUrlState";
import { deserializeNumberRange } from "./filters/utils";
import { useTableConfig } from "./useTableConfig";
import { DataTableGroupRow, DataTableDataRow } from "./DataTableRows";
import { getPrefixedParamName } from "./utils";
import { usePersistedColumnVisibility } from "./usePersistedColumnVisibility";
import { usePersistedPageSize } from "./usePersistedPageSize";
import { flexRender, useTable } from "@tanstack/react-table";
// Build the URL key for a chip filter. When a tableId is present we namespace
// to prevent collisions across multiple DataTables on the same page (e.g.,
// two tables both exposing a `name` column would have shared `name_filter`).
// Exported so external controls (e.g. toolbar toggle buttons) can target the
// same URL state instead of hand-mirroring the key convention.
export function chipFilterKey(columnId, tableId) {
    return tableId ? `${tableId}_${columnId}_filter` : `${columnId}_filter`;
}
function parseUrlFilters(search, getColumn, tableId) {
    const filters = [];
    const prefix = tableId ? `${tableId}_` : "";
    for (const key of Object.keys(search)){
        if (!key.endsWith("_filter")) continue;
        if (prefix && !key.startsWith(prefix)) continue;
        const columnId = key.slice(prefix.length, -"_filter".length);
        if (!columnId) continue;
        const value = search[key];
        if (value === undefined || value === "") continue;
        const filterType = getColumn(columnId)?.columnDef.meta?.filterType;
        let filterValue = value;
        if (filterType === "numberRange" && typeof value === "string") {
            const { min, max } = deserializeNumberRange(value);
            filterValue = {
                min: min ? parseFloat(min) : undefined,
                max: max ? parseFloat(max) : undefined
            };
        }
        filters.push({
            id: columnId,
            value: filterValue
        });
    }
    return filters;
}
function FilterChips({ enableUrlState, chips, newFilterChipId, onUpdate, onRemove }) {
    if (!enableUrlState || chips.length === 0) return null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: chips.map((chip)=>/*#__PURE__*/ _jsx(DataTableFilterChip, {
                column: chip.column,
                columnLabel: chip.columnLabel,
                filterValue: chip.filterValue,
                onUpdate: (value)=>onUpdate(chip.columnId, value),
                onRemove: ()=>onRemove(chip.columnId),
                autoFocus: newFilterChipId === chip.columnId
            }, chip.columnId))
    });
}
export function DataTable({ columns, data, isLoading = false, emptyMessage, enablePagination = true, enableSorting = true, enableColumnVisibility = false, enableRowSelection = false, enableGlobalFilter = false, enableColumnFilters = false, enableUrlState = false, onColumnFilterRequest, activeFilterFields, initialPageSize = 10, pageSizeOptions = [
    10,
    20,
    30,
    40,
    50
], className, onRowSelectionChange, toolbar, toolbarContent, globalSearchPlaceholder, quickFilters = [], tableId, initialColumnVisibility, initialSorting, initialColumnFilters, sorting: sortingProp, onSortingChange, enableExpanding = false, renderSubComponent, onRowClick, onRowHover, globalFilter: controlledGlobalFilter, onGlobalFilterChange: controlledOnGlobalFilterChange, getRowCanExpand: getRowCanExpandProp, initialGrouping, groupedByDefault = false, keepsLayoutWhenEmpty = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const search = useSearch({
        strict: false
    });
    // Single helper for the four URL writes below (global search + add/update/remove
    // chip). Keeps the navigate cast and replace:true in one place.
    const writeSearch = useCallback((mutator)=>{
        navigate({
            search: mutator,
            replace: true
        });
    }, [
        navigate
    ]);
    // Stable column reference using useMemo to prevent infinite re-renders
    const memoizedColumns = useMemo(()=>columns, [
        columns
    ]);
    // Page size is remembered per-table in localStorage (keyed by tableId) so
    // the user's choice survives logout/login, unlike the URL param. The URL
    // param is read from the raw search here (mirroring parseUrlState) because
    // useParseUrlState needs this hook's effective default as its fallback, so
    // the hook has to run first.
    const urlPageSizeParam = search[getPrefixedParamName("pageSize", tableId)];
    const [pageSize, setPageSize, defaultPageSize] = usePersistedPageSize({
        tableId,
        initialPageSize,
        urlPageSize: urlPageSizeParam ? Number(urlPageSizeParam) : undefined,
        enableUrlState
    });
    // Parse initial state from URL
    const urlState = useParseUrlState(enableUrlState, defaultPageSize, tableId);
    // Whether the consumer opted into grouping at all — exposes the toolbar toggle.
    const enableGrouping = (initialGrouping?.length ?? 0) > 0;
    // Active grouping state — off unless the consumer asks for it; the toolbar
    // switch flips it either way.
    const [grouping, setGrouping] = useState(groupedByDefault && enableGrouping ? initialGrouping : []);
    // State management
    const [ownSorting, setOwnSorting] = useState(enableUrlState && urlState.sorting.length > 0 ? urlState.sorting : initialSorting ?? []);
    const sorting = sortingProp ?? ownSorting;
    const setSorting = useCallback((updater)=>{
        const next = typeof updater === "function" ? updater(sorting) : updater;
        setOwnSorting(next);
        onSortingChange?.(next);
    }, [
        sorting,
        onSortingChange
    ]);
    // Chip URL keys populate this via the useEffect below.
    const [columnFilters, setColumnFilters] = useState(initialColumnFilters ?? []);
    // Column visibility is remembered per-table in localStorage (keyed by tableId)
    // so the user's show/hide choices survive reload and navigation. On mount the
    // saved preference layers over the code defaults; a shared link's hiddenColumns
    // can hide further on top, but (being hidden-only) never un-hides.
    const [columnVisibility, setColumnVisibility] = usePersistedColumnVisibility({
        tableId,
        initialColumnVisibility,
        urlColumnVisibility: urlState.columnVisibility,
        enableUrlState
    });
    // When grouping is active, hide the grouped column from data rows (value lives in
    // the group header). When the user toggles grouping off, the column reappears.
    const effectiveColumnVisibility = useMemo(()=>{
        if (grouping.length === 0) return columnVisibility;
        const overrides = {};
        for (const id of grouping)overrides[id] = false;
        return {
            ...columnVisibility,
            ...overrides
        };
    }, [
        columnVisibility,
        grouping
    ]);
    // TanStack expects a single pagination state; compose it from the split
    // pageIndex / pageSize states (page size is owned by usePersistedPageSize).
    const [pageIndex, setPageIndex] = useState(urlState.pageIndex);
    const pagination = useMemo(()=>({
            pageIndex,
            pageSize
        }), [
        pageIndex,
        pageSize
    ]);
    const setPagination = useCallback((updater)=>{
        const next = typeof updater === "function" ? updater({
            pageIndex,
            pageSize
        }) : updater;
        setPageIndex(next.pageIndex);
        setPageSize(next.pageSize);
    }, [
        pageIndex,
        pageSize,
        setPageSize
    ]);
    const [rowSelection, setRowSelection] = useState({});
    // Global filter: URL is canonical when enableUrlState, otherwise local state.
    // Derived (no useEffect needed for back-nav/refresh).
    const globalFilterUrlKey = getPrefixedParamName("q", tableId);
    const [localGlobalFilter, setLocalGlobalFilter] = useState("");
    const urlGlobalFilter = enableUrlState && typeof search[globalFilterUrlKey] === "string" ? search[globalFilterUrlKey] : "";
    const globalFilter = controlledGlobalFilter ?? (enableUrlState ? urlGlobalFilter : localGlobalFilter);
    const setGlobalFilter = useCallback((value)=>{
        if (controlledOnGlobalFilterChange) {
            controlledOnGlobalFilterChange(value);
            return;
        }
        if (!enableUrlState) {
            setLocalGlobalFilter(value);
            return;
        }
        writeSearch((prev)=>{
            const next = {
                ...prev
            };
            if (value === "") delete next[globalFilterUrlKey];
            else next[globalFilterUrlKey] = value;
            return next;
        });
    }, [
        controlledOnGlobalFilterChange,
        enableUrlState,
        globalFilterUrlKey,
        writeSearch
    ]);
    // Groups are open whenever grouping is on: from the start when the consumer
    // asked for it, and the toolbar toggle re-opens them (setExpanded(true)) the
    // moment the viewer turns grouping on.
    const [expanded, setExpanded] = useState(groupedByDefault && enableGrouping ? true : {});
    const [newFilterChipId, setNewFilterChipId] = useState(null);
    const handleAddFilter = useCallback((columnId)=>{
        if (!enableUrlState) return;
        // Text filters (and selects with options) are edited in a popover anchored
        // to the column header; auto-focusing the toolbar chip would steal focus
        // from it. All other cases still edit via the chip, so keep its focus.
        const col = memoizedColumns.find((c)=>{
            const id = typeof c.id === "string" ? c.id : String(c.accessorKey);
            return id === columnId;
        });
        const filterType = col?.meta?.filterType ?? "text";
        const anchoredEditorWillOpen = filterType === "text" || filterType === "select" && !!col?.meta?.filterOptions?.length;
        if (!anchoredEditorWillOpen) {
            setNewFilterChipId(columnId);
        }
        const filterKey = chipFilterKey(columnId, tableId);
        writeSearch((prev)=>({
                ...prev,
                [filterKey]: ""
            }));
    }, [
        enableUrlState,
        memoizedColumns,
        tableId,
        writeSearch
    ]);
    // The host's filter bar takes a column's request for its field.
    const handleColumnFilterRequest = useCallback((columnId)=>{
        const col = memoizedColumns.find((c)=>{
            const id = typeof c.id === "string" ? c.id : String(c.accessorKey);
            return id === columnId;
        });
        onColumnFilterRequest?.(col?.meta?.filterField ?? columnId);
    }, [
        memoizedColumns,
        onColumnFilterRequest
    ]);
    const handleUpdateFilter = useCallback((columnId, value)=>{
        if (!enableUrlState) return;
        const filterKey = chipFilterKey(columnId, tableId);
        writeSearch((prev)=>({
                ...prev,
                [filterKey]: value
            }));
    }, [
        enableUrlState,
        tableId,
        writeSearch
    ]);
    const handleRemoveFilter = useCallback((columnId)=>{
        if (!enableUrlState) return;
        const filterKey = chipFilterKey(columnId, tableId);
        writeSearch((prev)=>{
            const next = {
                ...prev
            };
            delete next[filterKey];
            return next;
        });
    }, [
        enableUrlState,
        tableId,
        writeSearch
    ]);
    // Build table configuration based on enabled features
    const tableConfig = useTableConfig({
        data,
        columns: memoizedColumns,
        sorting,
        columnFilters,
        columnVisibility: effectiveColumnVisibility,
        rowSelection,
        globalFilter,
        pagination,
        expanded,
        grouping,
        enablePagination,
        enableSorting,
        enableGlobalFilter,
        enableColumnFilters,
        enableColumnVisibility,
        enableRowSelection,
        enableExpanding,
        enableGrouping,
        setSorting,
        setPagination,
        setColumnFilters,
        setColumnVisibility,
        setRowSelection,
        setGlobalFilter,
        setExpanded,
        getRowCanExpand: getRowCanExpandProp
    });
    // Initialize table with TanStack Table
    const table = useTable(tableConfig);
    // useTable returns a fresh wrapper on every state change, so keying a hook on
    // table re-runs it each change (the columnFilters sync below would loop).
    const { getColumn, getFilteredSelectedRowModel } = table;
    const pageCount = table.getPageCount();
    useEffect(function resetPageIndexWhenOutOfRange() {
        if (pagination.pageIndex > 0 && pageCount > 0 && pagination.pageIndex >= pageCount) {
            setPageIndex(0);
        }
    }, [
        pageCount,
        pagination.pageIndex
    ]);
    // Sync table state changes back to URL (chip filters and global search write
    // their URL state directly, so they're not part of this debounced sync).
    // The saved page size is the user's effective default, so the URL only
    // carries deviations from it.
    useSyncTableStateToUrl({
        enableUrlState,
        initialPageSize: defaultPageSize,
        sorting,
        pagination,
        columnVisibility,
        tableId
    });
    useEffect(function notifyRowSelectionChange() {
        if (onRowSelectionChange && enableRowSelection) {
            const selectedRows = getFilteredSelectedRowModel().rows.map((row)=>row.original);
            onRowSelectionChange(selectedRows);
        }
    }, [
        rowSelection,
        onRowSelectionChange,
        enableRowSelection,
        getFilteredSelectedRowModel
    ]);
    useEffect(function syncUrlFiltersToColumnFilters() {
        if (!enableUrlState || !search) return;
        setColumnFilters(parseUrlFilters(search, getColumn, tableId));
    }, [
        enableUrlState,
        search,
        getColumn,
        tableId
    ]);
    /**
   * The filter a table opens with, written into the URL — once.
   *
   * Where `enableUrlState` is on, the URL is the only truth about filters, so
   * a default held beside it would be wiped by the sync above. It is written
   * on the FIRST mount only, tracked by a ref rather than by the URL being
   * empty: otherwise removing the chip would delete the parameter and the
   * effect would immediately put it back, which is a filter that cannot be
   * switched off.
   */ const defaultFiltersWritten = useRef(false);
    useEffect(()=>{
        if (!enableUrlState || defaultFiltersWritten.current) return;
        defaultFiltersWritten.current = true;
        const missing = (initialColumnFilters ?? []).filter((filter)=>!(chipFilterKey(filter.id, tableId) in (search ?? {})));
        if (missing.length === 0) return;
        writeSearch((prev)=>{
            const next = {
                ...prev
            };
            for (const filter of missing){
                next[chipFilterKey(filter.id, tableId)] = String(filter.value);
            }
            return next;
        });
    }, [
        enableUrlState,
        initialColumnFilters,
        search,
        tableId,
        writeSearch
    ]);
    // Get active filter chips from URL
    const activeFilterChips = useMemo(()=>{
        if (!enableUrlState || !search) return [];
        return memoizedColumns.filter((col)=>{
            const columnId = typeof col.id === "string" ? col.id : String(col.accessorKey);
            return chipFilterKey(columnId, tableId) in search;
        }).map((col)=>{
            const columnId = typeof col.id === "string" ? col.id : String(col.accessorKey);
            const filterKey = chipFilterKey(columnId, tableId);
            const column = getColumn(columnId);
            // Get label from meta, fallback to columnId
            const columnLabel = col.meta?.label || columnId;
            return {
                columnId,
                column,
                filterValue: search[filterKey],
                columnLabel: typeof columnLabel === "string" ? columnLabel : columnId
            };
        }).filter((chip)=>chip.column !== undefined); // Only include columns that exist in table
    }, [
        enableUrlState,
        search,
        memoizedColumns,
        getColumn,
        tableId
    ]);
    const showToolbar = [
        toolbar,
        toolbarContent,
        enableGlobalFilter,
        enableColumnVisibility,
        enableColumnFilters,
        enableGrouping,
        quickFilters.length > 0,
        activeFilterChips.length > 0
    ].some(Boolean);
    // Toolbar grouping toggle: label comes from the grouped column's meta;
    // toggling flips between the configured grouping and an empty (flat) state.
    const groupingToggle = enableGrouping ? {
        label: table.getColumn(initialGrouping[0])?.columnDef.meta?.label ?? initialGrouping[0],
        isGrouped: grouping.length > 0,
        onToggle: ()=>{
            setGrouping((current)=>current.length > 0 ? [] : initialGrouping);
            // Re-expand any groups that come back so the user sees content immediately.
            setExpanded(true);
        }
    } : undefined;
    // Show loading skeleton
    if (isLoading) {
        return /*#__PURE__*/ _jsx(DataTableSkeleton, {
            columnCount: columns.length,
            rowCount: initialPageSize,
            showPagination: enablePagination,
            className: className
        });
    }
    const toolbarElement = toolbar || showToolbar && /*#__PURE__*/ _jsx(DataTableToolbar, {
        table: table,
        enableGlobalFilter: enableGlobalFilter && !controlledOnGlobalFilterChange,
        globalFilterValue: globalFilter,
        onGlobalFilterChange: enableGlobalFilter ? setGlobalFilter : undefined,
        globalSearchPlaceholder: globalSearchPlaceholder,
        enableColumnVisibility: enableColumnVisibility,
        defaultColumnVisibility: initialColumnVisibility,
        quickFilters: quickFilters,
        groupingToggle: groupingToggle,
        filterChips: /*#__PURE__*/ _jsx(FilterChips, {
            enableUrlState: enableUrlState,
            chips: activeFilterChips,
            newFilterChipId: newFilterChipId,
            onUpdate: handleUpdateFilter,
            onRemove: handleRemoveFilter
        }),
        children: toolbarContent
    });
    if ((!data || data.length === 0) && !keepsLayoutWhenEmpty) {
        // A quick filter or a toolbar action works on the data from outside: it is
        // what narrowed the list to nothing, or what adds the first row. Hiding it
        // with the rows would leave no way back and no way to start.
        const keepsToolbar = Boolean(toolbar || toolbarContent) || quickFilters.length > 0;
        return keepsToolbar ? /*#__PURE__*/ _jsxs("div", {
            className: cn("w-full space-y-4", className),
            children: [
                toolbarElement,
                /*#__PURE__*/ _jsx(DataTableEmpty, {
                    message: emptyMessage
                })
            ]
        }) : /*#__PURE__*/ _jsx(DataTableEmpty, {
            message: emptyMessage,
            className: className
        });
    }
    // `keepsLayoutWhenEmpty`: an empty list is the same list without rows. The
    // message stands in the table, so two empty lists side by side look alike.
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("w-full space-y-4", className),
        children: [
            toolbarElement,
            /*#__PURE__*/ _jsx("div", {
                className: "border rounded-md",
                children: /*#__PURE__*/ _jsxs(Table, {
                    children: [
                        /*#__PURE__*/ _jsx(TableHeader, {
                            children: table.getHeaderGroups().map((headerGroup)=>/*#__PURE__*/ _jsx(TableRow, {
                                    children: headerGroup.headers.map((header)=>{
                                        const sticky = header.column.columnDef.meta?.sticky;
                                        // Pass onAddFilter to DataTableColumnHeader components
                                        const headerContent = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, {
                                            ...header.getContext(),
                                            sortedColumns: sorting.length,
                                            onAddFilter: enableUrlState ? handleAddFilter : onColumnFilterRequest ? handleColumnFilterRequest : undefined,
                                            externallyFiltered: (()=>{
                                                const field = header.column.columnDef.meta?.filterField;
                                                return !!field && !!activeFilterFields?.includes(field);
                                            })(),
                                            onUpdateFilter: enableUrlState ? handleUpdateFilter : undefined
                                        });
                                        const fitContent = header.column.columnDef.meta?.fitContent;
                                        return /*#__PURE__*/ _jsx(TableHead, {
                                            sticky: sticky,
                                            className: cn(fitContent && "w-0 whitespace-nowrap"),
                                            style: {
                                                maxWidth: header.column.columnDef.maxSize
                                            },
                                            children: headerContent
                                        }, header.id);
                                    })
                                }, headerGroup.id))
                        }),
                        /*#__PURE__*/ _jsx(TableBody, {
                            children: table.getRowModel().rows?.length ? table.getRowModel().rows.map((row)=>row.getIsGrouped() ? /*#__PURE__*/ _jsx(DataTableGroupRow, {
                                    row: row,
                                    colSpan: table.getVisibleLeafColumns().length
                                }, row.id) : /*#__PURE__*/ _jsx(DataTableDataRow, {
                                    row: row,
                                    renderSubComponent: renderSubComponent,
                                    onRowClick: onRowClick,
                                    onRowHover: onRowHover
                                }, row.id)) : /*#__PURE__*/ _jsx(TableRow, {
                                children: /*#__PURE__*/ _jsx(TableCell, {
                                    colSpan: columns.length,
                                    className: "h-24 text-center",
                                    children: emptyMessage || t("dataTable.noResults")
                                })
                            })
                        })
                    ]
                })
            }),
            enablePagination && /*#__PURE__*/ _jsx(DataTablePagination, {
                table: table,
                pageSizeOptions: pageSizeOptions
            })
        ]
    });
}

//# sourceMappingURL=DataTable.js.map